import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Purpose: `<VoiceWidget>` — embeddable React component that exposes
 *          one-click WebRTC voice with ada-backed OpenAI Realtime. Owns
 *          a `LlmClient`, a `RealtimeDriver`, and a small UI surface
 *          (mic button, status badge, live transcript, mute / stop
 *          controls). Audio media flows browser↔OpenAI; ada is the
 *          mint authority for the ephemeral token plus the bridge for
 *          server-side tool execution and cost capture.
 *
 * Implementation notes:
 *   - Inline styles only (no separate stylesheet). The widget is small
 *     enough that a CSS file is overkill, and inlining keeps the CDN
 *     bundle (@ada/voice-widget-cdn) self-contained without an extra
 *     asset to serve. Hosts that want to restyle pass a `className`
 *     and override via plain CSS — every panel element has a stable
 *     `data-ada-voice-*` attribute for targeting.
 *   - `tools` / `serverTools` mirror the copilot-widget contract so
 *     hosts can pass the same array to either widget. The driver
 *     resolves client tools locally and bridges server tools through
 *     `llm.realtime.tool.execute`.
 *   - The widget DOES NOT auto-connect on mount. The user clicks the
 *     mic button (which is a user gesture); only then do we call
 *     `getUserMedia` and `RealtimeDriver.connect()`. This satisfies
 *     browser autoplay policy and the principle of least astonishment.
 *   - Transcript state is kept in a flat array of `(role, text, final)`
 *     entries. Partial deltas append to the trailing assistant entry
 *     when the previous entry is non-final from the same role; on
 *     `final` they're replaced with the canonical text from the
 *     `response.output_audio_transcript.done` event (GA name; the driver
 *     also accepts the beta `response.audio_transcript.done`).
 *
 * Created: 2026-05-16 13:00
 *
 * Changelog:
 *   - 2026-05-16 13:00 — Initial version. V1 surface: mic toggle,
 *     status badge, live transcript, mute, stop. Pairs with the
 *     CDN bundle in @ada/voice-widget-cdn.
 *   - 2026-05-16 10:40 — Import `JSX` from `react` so return-type
 *     annotations (`: JSX.Element`) resolve under @types/react@19,
 *     which no longer exposes JSX as a global namespace.
 *   - 2026-05-18 18:30 — Fix: in popup layout, the panel was
 *     auto-collapsing the moment the driver emitted `status: 'closed'`,
 *     which the connect() failure path always emits right after the
 *     `error` event. The error message was set but the panel had
 *     already disappeared, so users only saw a popup flash open and
 *     vanish. We now track `hadErrorRef` per session and keep the
 *     panel open when the session ended in an error so the error UI
 *     remains visible; clean stops still auto-collapse to the
 *     launcher as before.
 *   - 2026-05-18 21:30 — Expand the props surface to mirror every
 *     option `llm.realtime.session.create` accepts: `audioInput`,
 *     `audioOutput`, `toolChoice`, `outputModalities`, `temperature`,
 *     `maxOutputTokens`, `tracing`, `prompt`, `include`,
 *     `sessionOverrides`, `expiresInSeconds`. Legacy `voice`,
 *     `maxResponseOutputTokens`, `maxSessionSeconds` stay accepted
 *     and forward into the GA-shaped slots in the underlying driver.
 *   - 2026-06-01 13:30 — Push client-tool changes to the live session.
 *     A new effect keyed on the memoised registry calls
 *     `RealtimeDriver.updateClientTools()` so swapping the `tools` prop
 *     mid-call now takes effect via `session.update` instead of being
 *     silently ignored until the next session. Server tools remain
 *     fixed at mint.
 *   - 2026-10-01 15:40 — Comment only: name the GA transcript event the
 *     driver now handles. No behaviour change in this file. With the driver
 *     fix, the assistant transcript and 'speaking' badge render again.
 */
import { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { ClientToolRegistry, RealtimeDriver, } from '@ada/copilot-core';
import { LlmClient } from '@ada/llm-client';
// ── Component ───────────────────────────────────────────────────
export function VoiceWidget(props) {
    const { appId, auth, apiUrl, model, instructions, serverTools, tools, toolChoice, outputModalities, temperature, maxOutputTokens, tracing, prompt, include, audioInput, audioOutput, sessionOverrides, expiresInSeconds, layout = 'popup', title = 'Voice assistant', onTranscript, onToolCall, onUsage, onError, className, style, 
    // Back-compat.
    voice, maxResponseOutputTokens, maxSessionSeconds, } = props;
    const [status, setStatus] = useState('idle');
    const [open, setOpen] = useState(layout === 'inline');
    const [muted, setMuted] = useState(false);
    const [transcript, setTranscript] = useState([]);
    const [errorMessage, setErrorMessage] = useState();
    const driverRef = useRef(null);
    const transcriptIdRef = useRef(0);
    // Tracks whether the active session has emitted an `error` event.
    // The driver's connect() failure path emits `error` → `status: 'closed'`
    // back-to-back, and we must NOT collapse the popup on `closed` if the
    // user still has an error to read. Reset at the start of each session.
    const hadErrorRef = useRef(false);
    // Build the LlmClient once per (auth, apiUrl) change.
    const llm = useMemo(() => new LlmClient({
        url: apiUrl ?? '/api',
        ...(auth !== undefined ? { auth } : {}),
    }), [apiUrl, auth]);
    // Build a registry from the `tools` prop. Memoised on the reference
    // identity of the array, so hosts must wrap their tools in useMemo
    // (same contract as @ada/copilot-widget).
    const registry = useMemo(() => {
        const r = new ClientToolRegistry();
        for (const t of tools ?? [])
            r.register(t);
        return r;
    }, [tools]);
    const handleEvent = useCallback((event) => {
        switch (event.type) {
            case 'status':
                setStatus(event.status);
                // Only auto-collapse the popup on a clean session close. If
                // the session ended because of an error, keep the panel open
                // so the user can actually read what went wrong before
                // dismissing it manually via the Close button.
                if (event.status === 'closed' && !hadErrorRef.current) {
                    setOpen(layout === 'inline');
                }
                break;
            case 'transcript':
                onTranscript?.({
                    role: event.role,
                    delta: event.delta,
                    final: event.final,
                });
                setTranscript((prev) => appendTranscript(prev, event, transcriptIdRef));
                break;
            case 'tool-call':
                onToolCall?.({
                    name: event.name,
                    arguments: event.arguments,
                    origin: event.origin,
                });
                break;
            case 'usage':
                onUsage?.({
                    inputTokens: event.inputTokens,
                    outputTokens: event.outputTokens,
                });
                break;
            case 'error':
                hadErrorRef.current = true;
                setErrorMessage(event.message);
                // Defensive: surface the panel even if a stale `closed` event
                // had already collapsed it in popup mode. Without this, an
                // error that arrives just after a `closed` would be invisible.
                if (layout === 'popup') {
                    setOpen(true);
                }
                onError?.({ ...(event.code ? { code: event.code } : {}), message: event.message });
                break;
            default:
                break;
        }
    }, [layout, onTranscript, onToolCall, onUsage, onError]);
    // Tear down the driver when the component unmounts.
    useEffect(() => {
        return () => {
            void driverRef.current?.disconnect();
            driverRef.current = null;
        };
    }, []);
    // Push client-tool changes to the *live* session. `registry` is
    // memoised on the `tools` prop identity, so this fires only when the
    // host actually swaps its tool set — not on every render. When no
    // session is active the optional-chain is a no-op, and the next
    // `start()` picks up the latest registry anyway; on the initial
    // connect the tools are already baked into the mint, so there is no
    // redundant update. NOTE: this covers *client* tools only — server
    // tools are fixed at mint (their schemas live server-side); changing
    // `serverTools` mid-session still requires a stop()/start() reconnect.
    useEffect(() => {
        driverRef.current?.updateClientTools(registry.list());
    }, [registry]);
    const start = useCallback(async () => {
        if (driverRef.current)
            return;
        hadErrorRef.current = false;
        setErrorMessage(undefined);
        setTranscript([]);
        const driver = new RealtimeDriver({
            llm,
            toolRegistry: registry,
            ...(serverTools !== undefined ? { serverTools } : {}),
            ...(model !== undefined ? { model } : {}),
            ...(instructions !== undefined ? { instructions } : {}),
            ...(toolChoice !== undefined ? { toolChoice } : {}),
            ...(outputModalities !== undefined ? { outputModalities } : {}),
            ...(temperature !== undefined ? { temperature } : {}),
            ...(maxOutputTokens !== undefined ? { maxOutputTokens } : {}),
            ...(tracing !== undefined ? { tracing } : {}),
            ...(prompt !== undefined ? { prompt } : {}),
            ...(include !== undefined ? { include } : {}),
            ...(audioInput !== undefined ? { audioInput } : {}),
            ...(audioOutput !== undefined ? { audioOutput } : {}),
            ...(sessionOverrides !== undefined ? { sessionOverrides } : {}),
            ...(expiresInSeconds !== undefined ? { expiresInSeconds } : {}),
            // Back-compat.
            ...(voice !== undefined ? { voice } : {}),
            ...(maxResponseOutputTokens !== undefined ? { maxResponseOutputTokens } : {}),
            ...(maxSessionSeconds !== undefined ? { maxSessionSeconds } : {}),
        });
        driver.on(handleEvent);
        driverRef.current = driver;
        setOpen(true);
        try {
            await driver.connect();
        }
        catch {
            // Errors already surfaced via the 'error' event listener; the
            // driver's connect() rejection is duplicate signal here. Clear
            // the ref so the user can click Start again to retry — a
            // RealtimeDriver instance is single-use (its disconnect() sets
            // #closed=true, and connect() then throws "already closed").
            if (driverRef.current === driver) {
                driverRef.current = null;
            }
        }
    }, [
        llm,
        registry,
        serverTools,
        model,
        instructions,
        toolChoice,
        outputModalities,
        temperature,
        maxOutputTokens,
        tracing,
        prompt,
        include,
        audioInput,
        audioOutput,
        sessionOverrides,
        expiresInSeconds,
        voice,
        maxResponseOutputTokens,
        maxSessionSeconds,
        handleEvent,
    ]);
    const stop = useCallback(async () => {
        const driver = driverRef.current;
        if (!driver)
            return;
        driverRef.current = null;
        await driver.disconnect();
    }, []);
    const toggleMute = useCallback(() => {
        const driver = driverRef.current;
        if (!driver)
            return;
        setMuted((prev) => {
            const next = !prev;
            driver.mute(next);
            return next;
        });
    }, []);
    const isActive = status === 'connecting' || status === 'listening' || status === 'speaking' || status === 'tool';
    const rootStyle = layout === 'inline' ? rootInlineStyle : rootPopupStyle;
    // The launcher (when collapsed in popup mode) is a single round
    // button. The expanded panel renders the same button as a stop
    // affordance in the header.
    if (layout === 'popup' && !open) {
        return (_jsx("button", { type: "button", "data-ada-voice": "launcher", "data-ada-voice-app-id": appId, onClick: () => {
                void start();
            }, "aria-label": "Start voice assistant", style: launcherStyle, className: className, children: micIcon(20) }));
    }
    return (_jsxs("div", { "data-ada-voice": "root", "data-ada-voice-app-id": appId, "data-ada-voice-status": status, style: { ...rootStyle, ...(style ?? {}) }, className: className, children: [_jsxs("div", { style: headerStyle, children: [_jsx("span", { style: titleStyle, children: title }), _jsx("span", { "data-ada-voice": "status", style: statusBadgeStyle(status), children: statusLabel(status) })] }), _jsx("div", { "data-ada-voice": "transcript", style: transcriptStyle, children: transcript.length === 0 ? (_jsx("div", { style: transcriptEmptyStyle, children: isActive ? 'Listening… speak when you are ready.' : 'Press the mic to start speaking.' })) : (transcript.map((entry) => (_jsx("div", { "data-ada-voice-role": entry.role, style: entry.role === 'user' ? userBubbleStyle : assistantBubbleStyle, children: entry.text }, entry.id)))) }), errorMessage !== undefined && (_jsx("div", { "data-ada-voice": "error", style: errorStyle, children: errorMessage })), _jsxs("div", { style: footerStyle, children: [_jsx("button", { type: "button", "data-ada-voice": "mute", onClick: toggleMute, disabled: !isActive, "aria-pressed": muted, "aria-label": muted ? 'Unmute microphone' : 'Mute microphone', style: iconButtonStyle(muted), children: muted ? micOffIcon(18) : micIcon(18) }), isActive ? (_jsx("button", { type: "button", "data-ada-voice": "stop", onClick: () => void stop(), "aria-label": "End voice session", style: primaryButtonStyle, children: "Stop" })) : (_jsx("button", { type: "button", "data-ada-voice": "start", onClick: () => void start(), "aria-label": "Start voice session", style: primaryButtonStyle, children: "Start" })), layout === 'popup' && (_jsx("button", { type: "button", "data-ada-voice": "close", onClick: () => {
                            void stop();
                            setOpen(false);
                        }, "aria-label": "Close panel", style: ghostButtonStyle, children: "Close" }))] })] }));
}
// ── Transcript helpers ─────────────────────────────────────────
function appendTranscript(prev, event, idRef) {
    const tail = prev[prev.length - 1];
    if (event.final) {
        // Final transcript: if the last entry is a non-final from the same
        // role, replace it; otherwise append a fresh final entry.
        if (tail && tail.role === event.role && !tail.final) {
            const copy = prev.slice(0, -1);
            copy.push({ ...tail, text: event.delta || tail.text, final: true });
            return copy;
        }
        if (event.delta) {
            idRef.current += 1;
            return [...prev, { id: idRef.current, role: event.role, text: event.delta, final: true }];
        }
        return prev;
    }
    // Delta: append to a trailing same-role non-final entry, else start
    // a fresh non-final entry.
    if (tail && tail.role === event.role && !tail.final) {
        const copy = prev.slice(0, -1);
        copy.push({ ...tail, text: tail.text + event.delta });
        return copy;
    }
    idRef.current += 1;
    return [...prev, { id: idRef.current, role: event.role, text: event.delta, final: false }];
}
// ── Status helpers ─────────────────────────────────────────────
function statusLabel(status) {
    switch (status) {
        case 'idle':
            return 'Idle';
        case 'connecting':
            return 'Connecting…';
        case 'listening':
            return 'Listening';
        case 'speaking':
            return 'Speaking';
        case 'tool':
            return 'Calling tool…';
        case 'closed':
            return 'Closed';
        case 'error':
            return 'Error';
        default:
            return status;
    }
}
// ── Inline styles ──────────────────────────────────────────────
const rootPopupStyle = {
    position: 'fixed',
    bottom: '1.25rem',
    right: '1.25rem',
    width: '22rem',
    maxHeight: '70vh',
    display: 'flex',
    flexDirection: 'column',
    background: 'white',
    color: '#1a1a1a',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    borderRadius: '0.75rem',
    boxShadow: '0 12px 32px rgba(0, 0, 0, 0.18)',
    fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, Ubuntu, sans-serif",
    fontSize: '0.875rem',
    zIndex: 2147483646,
    overflow: 'hidden',
};
const rootInlineStyle = {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    background: 'white',
    color: '#1a1a1a',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    borderRadius: '0.5rem',
    fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, Ubuntu, sans-serif",
    fontSize: '0.875rem',
};
const launcherStyle = {
    position: 'fixed',
    bottom: '1.25rem',
    right: '1.25rem',
    width: '3rem',
    height: '3rem',
    borderRadius: '999px',
    background: '#0b5fff',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 8px 22px rgba(11, 95, 255, 0.4)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2147483646,
};
const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.625rem 0.875rem',
    borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
    background: '#fafafa',
};
const titleStyle = {
    fontWeight: 600,
    fontSize: '0.875rem',
};
function statusBadgeStyle(status) {
    const palette = {
        idle: '#999',
        connecting: '#b58900',
        listening: '#0b5fff',
        speaking: '#1b9e3a',
        tool: '#8a3ffc',
        closed: '#666',
        error: '#c0392b',
    };
    return {
        fontSize: '0.75rem',
        fontWeight: 500,
        color: 'white',
        background: palette[status] ?? '#666',
        padding: '0.125rem 0.5rem',
        borderRadius: '999px',
    };
}
const transcriptStyle = {
    flex: '1 1 auto',
    overflowY: 'auto',
    padding: '0.75rem 0.875rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
};
const transcriptEmptyStyle = {
    color: '#888',
    fontSize: '0.8125rem',
    fontStyle: 'italic',
    textAlign: 'center',
    marginTop: '1rem',
};
const userBubbleStyle = {
    alignSelf: 'flex-end',
    maxWidth: '85%',
    padding: '0.4rem 0.625rem',
    borderRadius: '0.6rem 0.6rem 0.1rem 0.6rem',
    background: '#0b5fff',
    color: 'white',
    fontSize: '0.8125rem',
    lineHeight: 1.4,
};
const assistantBubbleStyle = {
    alignSelf: 'flex-start',
    maxWidth: '85%',
    padding: '0.4rem 0.625rem',
    borderRadius: '0.6rem 0.6rem 0.6rem 0.1rem',
    background: '#f3f3f4',
    color: '#1a1a1a',
    fontSize: '0.8125rem',
    lineHeight: 1.4,
};
const errorStyle = {
    padding: '0.5rem 0.875rem',
    background: '#fff4f4',
    color: '#a3271b',
    fontSize: '0.75rem',
    borderTop: '1px solid #f2c4be',
};
const footerStyle = {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
    padding: '0.5rem 0.875rem',
    borderTop: '1px solid rgba(0, 0, 0, 0.06)',
    background: '#fafafa',
};
function iconButtonStyle(active) {
    return {
        width: '2rem',
        height: '2rem',
        borderRadius: '999px',
        background: active ? '#c0392b' : '#ececef',
        color: active ? 'white' : '#333',
        border: 'none',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
    };
}
const primaryButtonStyle = {
    background: '#0b5fff',
    color: 'white',
    border: 'none',
    borderRadius: '0.4rem',
    padding: '0.4rem 0.875rem',
    fontWeight: 600,
    fontSize: '0.8125rem',
    cursor: 'pointer',
};
const ghostButtonStyle = {
    marginLeft: 'auto',
    background: 'transparent',
    color: '#666',
    border: 'none',
    fontSize: '0.8125rem',
    cursor: 'pointer',
};
// ── Tiny SVG icons (inline, no external assets) ────────────────
function micIcon(size) {
    return (_jsxs("svg", { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", children: [_jsx("path", { d: "M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" }), _jsx("path", { d: "M19 10v2a7 7 0 0 1-14 0v-2" }), _jsx("line", { x1: "12", y1: "19", x2: "12", y2: "23" }), _jsx("line", { x1: "8", y1: "23", x2: "16", y2: "23" })] }));
}
function micOffIcon(size) {
    return (_jsxs("svg", { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", children: [_jsx("line", { x1: "1", y1: "1", x2: "23", y2: "23" }), _jsx("path", { d: "M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" }), _jsx("path", { d: "M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" }), _jsx("line", { x1: "12", y1: "19", x2: "12", y2: "23" }), _jsx("line", { x1: "8", y1: "23", x2: "16", y2: "23" })] }));
}
//# sourceMappingURL=VoiceWidget.js.map